import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { CatalogPaintCutType } from "../models/CatalogPaintCutType";
import { getCatalogPaintCutTypes } from "../repositories/CatalogPaintCutTypeRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const usePaintCutTypes = (onError?: (error: Error) => void) => {
	return useQuery<CatalogPaintCutType[], Error>({
		queryKey: ["catalogPaintCutTypes"],
		queryFn: () => getCatalogPaintCutTypes(),
		onError: onError,
	});
};

export const PaintCutTypeSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: paintCutTypes, isLoading } = usePaintCutTypes((error) =>
		setError(error.message),
	);

	const selectedPaintCutType =
		paintCutTypes?.find((p) => p.PaintCutTypeId === value) || null;

	return (
		<>
			<Autocomplete
				id="paint-cut-type-selector"
				fullWidth
				clearOnEscape
				options={paintCutTypes || []}
				getOptionLabel={(option) => option.PaintCutTypeName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedPaintCutType}
				onChange={(event, newValue) => {
					onChange(newValue?.PaintCutTypeId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading paint cut types"
			/>
		</>
	);
};
